import '../css/RunQueue.less';

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {Header} from 'semantic-ui-react';
import {RunQueueItem} from '../generated/graphql';
import makeComp from '../util/profiler';

const SPAN_PADDING = 40;

interface RunConfig {
  [key: string]: string | number;
}

interface RunQueueTabProps {
  entityName: string;
  projectName: string;
  runQueueItems?: RunQueueItem[];
  refetchProject: () => void;
}

const RunQueueTab: React.FC<RunQueueTabProps> = makeComp(
  props => {
    if (props.runQueueItems === undefined) {
      return null;
    }
    return (
      <RunQueueTabContents {...props} runQueueItems={props.runQueueItems} />
    );
  },
  {id: 'RunQueueTab'}
);

export default RunQueueTab;

interface RunQueueTabContentsProps extends RunQueueTabProps {
  runQueueItems: RunQueueItem[];
}

const RunQueueTabContents: React.FC<RunQueueTabContentsProps> = makeComp(
  ({runQueueItems: unfiltered, refetchProject}) => {
    useEffect(() => {
      refetchProject();
    }, [refetchProject]);

    const runQueueItems = unfiltered.filter(
      i => i.runSpec.input?.config?.length
    );
    const allRuns = runQueueItems.map(i => i.runSpec.input.config[0]);
    const pendingRunConfigs = runQueueItems
      .filter(i => i.state === 'PENDING')
      .map(i => i.runSpec.input.config[0]);
    const runningRuncConfigs = runQueueItems
      .filter(i => i.state === 'LEASED')
      .map(i => i.runSpec.input.config[0]);

    const [colWidths, setColWidths] = useState<number[]>([]);

    const getColWidth = (colI: number) =>
      colWidths[colI] == null ? 'auto' : colWidths[colI] + SPAN_PADDING;

    const reportInitialWidth = useCallback((colI: number, w: number) => {
      setColWidths(prevColWidths => {
        const maxW = Math.max(prevColWidths[colI] ?? 0, w);
        if (maxW <= 0) {
          return prevColWidths;
        }
        const newColWidths = [...prevColWidths];
        newColWidths[colI] = maxW;
        return newColWidths;
      });
    }, []);

    if (runQueueItems.length === 0) {
      return null;
    }

    const runKeyMap: {[key: string]: true} = {};
    allRuns.forEach(r => Object.keys(r).forEach(k => (runKeyMap[k] = true)));
    const colNames = ['No.', ...Object.keys(runKeyMap)];

    const runQueue = (configs: RunConfig[]) => (
      <div className="run-queue">
        <div className="run-queue-header">
          {colNames.map((col, i) => (
            <SyncedWidthSpan
              key={col}
              colI={i}
              width={getColWidth(i)}
              reportInitialWidth={reportInitialWidth}>
              {col}
            </SyncedWidthSpan>
          ))}
        </div>
        {configs.map((config, runI) => (
          <div key={runI} className="run-queue-item">
            <SyncedWidthSpan
              colI={0}
              width={getColWidth(0)}
              reportInitialWidth={reportInitialWidth}>
              {runI + 1}
            </SyncedWidthSpan>
            {colNames.slice(1).map((k, i) => (
              <SyncedWidthSpan
                key={`${runI}-${i}`}
                colI={i + 1}
                width={getColWidth(i + 1)}
                reportInitialWidth={reportInitialWidth}>
                {config[k] ?? ''}
              </SyncedWidthSpan>
            ))}
          </div>
        ))}
      </div>
    );

    return (
      <div className="run-queue-tab">
        <Header as="h3">Now Running</Header>
        {runningRuncConfigs.length > 0 && runQueue(runningRuncConfigs)}
        <Header className="up-next-header" as="h3">
          Up Next
        </Header>
        {pendingRunConfigs.length > 0 && runQueue(pendingRunConfigs)}
      </div>
    );
  },
  {id: 'RunQueueTabContents'}
);

interface SyncedWidthSpanProps {
  colI: number;
  width: number | 'auto';
  reportInitialWidth: (colI: number, width: number) => void;
}

const SyncedWidthSpan: React.FC<SyncedWidthSpanProps> = makeComp(
  ({colI, width, reportInitialWidth, children}) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const reportedRef = useRef(false);

    useLayoutEffect(() => {
      if (!spanRef.current || reportedRef.current) {
        return;
      }
      reportInitialWidth(colI, spanRef.current.offsetWidth);
      reportedRef.current = true;
    }, [colI, reportInitialWidth]);

    const style = reportedRef.current ? {width} : {};

    return (
      <span ref={spanRef} style={style}>
        {children}
      </span>
    );
  },
  {id: 'SyncedWidthSpan'}
);
